import { useState, useEffect } from "react";
import { platformService } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Coins, Sparkles, Check, ShoppingCart, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  currency: string;
}

interface CreditPurchaseProps {
  tenantId: string;
  onPurchaseComplete?: () => void;
}

const CreditPurchase = ({ tenantId, onPurchaseComplete }: CreditPurchaseProps) => {
  const { toast } = useToast();
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    setLoading(true);
    const { data } = await platformService.listCreditPacks();
    setPacks((data as CreditPack[]) || []);
    setLoading(false);
  };

  const formatPrice = (cents: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  };

  const handlePurchase = async () => {
    if (!selectedPack || !tenantId) return;

    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-credit-checkout", {
        body: { creditPackId: selectedPack, tenantId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast({
        title: "Payment Error",
        description: err.message || "Could not start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPurchasing(false);
    }
  };

  if (loading || packs.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Coins className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-sm">Need More Generations?</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Purchase additional avatar generation credits to keep creating.
      </p>

      <div className="grid gap-3">
        {packs.map((pack) => {
          const isPopular = pack.name.toLowerCase() === "popular";
          return (
            <button
              key={pack.id}
              onClick={() => setSelectedPack(pack.id)}
              className={cn(
                "relative flex items-center justify-between rounded-xl border bg-card p-4 transition-all hover:shadow-card text-left",
                selectedPack === pack.id
                  ? "ring-2 ring-primary border-primary"
                  : "border-border"
              )}
            >
              {isPopular && (
                <Badge className="absolute -top-2 left-3 text-[10px] px-2 py-0.5 bg-primary text-primary-foreground">
                  Most Popular
                </Badge>
              )}
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{pack.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {pack.credits} credit{pack.credits !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm">
                  {formatPrice(pack.price_cents, pack.currency)}
                </span>
                {selectedPack === pack.id && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Button
        className="w-full gradient-primary text-primary-foreground font-semibold gap-2"
        disabled={!selectedPack || purchasing}
        onClick={handlePurchase}
      >
        {purchasing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ShoppingCart className="h-4 w-4" />
        )}
        {purchasing ? "Opening Checkout..." : "Purchase Credits"}
      </Button>

      <p className="text-[10px] text-muted-foreground text-center">
        Payments processed securely via Stripe. Powered by Cardmoji.
      </p>
    </div>
  );
};

export default CreditPurchase;
